import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  TenantLifecycleSnapshotWire,
  TenantLifecycleHistoryEventWire,
} from '../lib/api-client';

export interface TenantLifecycleDetailResult {
  tenant: TenantLifecycleSnapshotWire;
  history: TenantLifecycleHistoryEventWire[];
}

/**
 * useTenantLifecycleDetail — React Query para GET /v1/tenants/:id/lifecycle.
 *
 * Retorna snapshot + history de um Tenant específico.
 * Consome capability `tenant-lifecycle-read`.
 *
 * W3.3 Sprint Frontend Desktop — T13 Tenant Lifecycle Dashboard.
 */
export function useTenantLifecycleDetail(tenantId: string | null): {
  data: TenantLifecycleDetailResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['tenant-lifecycle', 'detail', tenantId],
    queryFn: () => {
      if (!tenantId) throw new Error('tenantId required');
      return api.getTenantLifecycleDetail(tenantId);
    },
    enabled: tenantId !== null,
    staleTime: 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/**
 * useTenantLifecycleMutations — mutations para transições manuais.
 *
 * Invalida queryKey ['tenant-lifecycle'] após cada operação bem-sucedida.
 * If-Match (version) passado explicitamente pelo caller — defensivo
 * vs race conditions (ADR-071).
 *
 * Decisão: initiateChurning despacha para endpoint W2.3 que aciona
 * AnonymizationService async — frontend só aguarda `200 OK` com
 * snapshot atualizado; progresso LGPD via churningProgressJson
 * (candidato Item futuro: WS event `tenant.lifecycle.churning.progress`).
 */
export function useTenantLifecycleMutations(tenantId: string): {
  transition: (
    body: { toStatus: string; reason?: string | null },
    version: number,
  ) => Promise<void>;
  initiateChurning: (
    body: {
      reason: string;
      anonymizationLevel?: 'mask' | 'pseudonymize' | 'hash_irreversible';
      cancellablePeriodDays?: number;
    },
    version: number,
  ) => Promise<void>;
  cancelChurning: (body: { reason: string }, version: number) => Promise<void>;
  isTransitioning: boolean;
  isInitiatingChurning: boolean;
  isCancellingChurning: boolean;
  transitionError: unknown;
  churningError: unknown;
  cancelError: unknown;
} {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['tenant-lifecycle'] });
  };

  const transitionMut = useMutation({
    mutationFn: ({
      body,
      version,
    }: {
      body: { toStatus: string; reason?: string | null };
      version: number;
    }) => api.transitionTenantStatus(tenantId, body, version),
    onSuccess: invalidate,
  });

  const initiateMut = useMutation({
    mutationFn: ({
      body,
      version,
    }: {
      body: {
        reason: string;
        anonymizationLevel?: 'mask' | 'pseudonymize' | 'hash_irreversible';
        cancellablePeriodDays?: number;
      };
      version: number;
    }) => api.initiateChurning(tenantId, body, version),
    onSuccess: invalidate,
  });

  const cancelMut = useMutation({
    mutationFn: ({ body, version }: { body: { reason: string }; version: number }) =>
      api.cancelChurning(tenantId, body, version),
    onSuccess: invalidate,
  });

  return {
    transition: async (body, version) => {
      await transitionMut.mutateAsync({ body, version });
    },
    initiateChurning: async (body, version) => {
      await initiateMut.mutateAsync({ body, version });
    },
    cancelChurning: async (body, version) => {
      await cancelMut.mutateAsync({ body, version });
    },
    isTransitioning: transitionMut.isPending,
    isInitiatingChurning: initiateMut.isPending,
    isCancellingChurning: cancelMut.isPending,
    transitionError: transitionMut.error,
    churningError: initiateMut.error,
    cancelError: cancelMut.error,
  };
}
